import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { ICobertura } from '@interfaces/cobertura.interface';
import { IDni } from '@interfaces/dni.interface';
import { IIban } from '@interfaces/iban.interface';
import { IPolicyRequest } from '@interfaces/policy-request.interface';
import { IPolicyResponse } from '@interfaces/policy-response.interface';
import { ISiniestroRequest } from '@interfaces/siniestro-request.interface';
import { ISiniestroResponse } from '@interfaces/siniestro-response.interface';
import { ITarifa } from '@interfaces/tarifa.interface';
import { ITipoExpediente } from '@interfaces/tipo-expediente.interface';
import { ITitularPolizaDatos } from '@interfaces/titularPoliza-datos.interface';
import { IValidarIban } from '@interfaces/validar-iban.interface';
import { IValidarSiniestro } from '@interfaces/validar-siniestro.interface';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class InsuranceService {
  baseUrl = environment.DOMAIN;
  constructor(private http: HttpClient) {}

  hireInsurance(body: IPolicyRequest): Observable<any> {
    const headers = new HttpHeaders();
    return this.http.post(
      `${this.baseUrl}/${environment.API.WEB.GUARDAR_DATOS_WEB}`, // URL del endpoint
      body,
      { headers }
    );
  }

  getPolicyData(num_documento: string): Observable<IPolicyResponse> {
    const headers = new HttpHeaders();
    let params = new HttpParams().set('num_documento', num_documento);
    return this.http.get<IPolicyResponse>(
      `${this.baseUrl}/${environment.API.WEB.OBTENER_DATOS_WEB}`,
      { headers, params }
    );
  }

  obtenerPolizas(num_documento: string): Observable<IPolicyResponse[]>
  {
    const headers = new HttpHeaders();
    let params = new HttpParams().set('num_documento', num_documento);
    return this.http.get<IPolicyResponse[]>(
      `${this.baseUrl}/${environment.API.WEB.OBTENER_POLIZAS}`,
      { headers, params }
    );
  }

  modificarDatosPoliza(
    idPoliza: number,
    datos: IPolicyRequest
  ): Observable<any> {
    const headers = new HttpHeaders();
    return this.http.post(
      `${this.baseUrl}/${environment.API.WEB.MODIFICAR_DATOS_WEB}?IdPoliza=${idPoliza}`,
      datos,
      { headers }
    );
  }

  suspenderPoliza(idPoliza: number, datos: IPolicyRequest): Observable<any> {
    console.log(datos);
    const headers = new HttpHeaders();
    return this.http.post(
      `${this.baseUrl}/${environment.API.WEB.SUSPENDER_POLIZA}?IdPoliza=${idPoliza}`,
      datos,
      { headers }
    );
  }

  validarDni(numDocumento: string): Observable<IDni> {
    const headers = new HttpHeaders();
    return this.http.get<IDni>(
      `${this.baseUrl}/${environment.API.WEB.VALIDAD_DNI}?num_documento=${numDocumento}`
    );
  }

  obtenerTarifas(idTipoPoliza: number): Observable<ITarifa> {
    const headers = new HttpHeaders();
    const params = new HttpParams().set(
      'idTipoPoliza',
      idTipoPoliza.toString()
    );
    return this.http.get<ITarifa>(
      `${this.baseUrl}/${environment.API.WEB.OBTENER_TARIFAS}`,
      { headers, params }
    );
  }

  validarIbanPoiza(datosIbanValidar: IValidarIban): Observable<any> {
    const headers = new HttpHeaders();
    return this.http.post<any>(
      `${this.baseUrl}/${environment.API.WEB.VALIDAR_IBAN_POLIZA}`,
      datosIbanValidar,
      { headers, responseType: 'text' as 'json' }
    );
  }

  descargarFicheroPoliza(idPoliza: number) {
    const headers = new HttpHeaders();
    const params = new HttpParams().set('IdPoliza', idPoliza);

    return this.http.get(
      `${this.baseUrl}/${environment.API.WEB.OBTENER_DOCUMENTO_POLIZA}`,
      { headers, params }
    );
  }

  obtenerSiniestros(params: { idPoliza?: number; numDocumento?: string }): Observable<ISiniestroResponse[]> {
    const headers = new HttpHeaders();
    const httpParams = new HttpParams({ fromObject: {
    ...(params.idPoliza ? { idPoliza: params.idPoliza.toString() } : {}),
    ...(params.numDocumento ? { numDocumento: params.numDocumento } : {})
  }});
    return this.http.get<ISiniestroResponse[]>(
      `${this.baseUrl}/${environment.API.SINIESTRO.OBTENER_SINIESTROS}`,
      { headers,  params: httpParams }
    );
  }

  validarAperturaSiniestro(idPoliza: number): Observable<IValidarSiniestro> {
    const headers = new HttpHeaders();
    const params = new HttpParams().set('IdPoliza', idPoliza.toString());
    return this.http.get<IValidarSiniestro>(
      `${this.baseUrl}/${environment.API.SINIESTRO.VALIDAR_APERTURA_SINIESTRO}`,
      { headers, params }
    );
  }

  /**
   * Obtiene los datos del titular de la póliza por su IdPoliza
   * @param idPoliza número de póliza a consultar
   * @returns Observable de datos del titular de la póliza
   */
  obtenerDatosTitularPoliza(idPoliza: number): Observable<ITitularPolizaDatos> {
    const headers = new HttpHeaders();
    const params = new HttpParams().set('IdPoliza', idPoliza.toString());
    return this.http.get<ITitularPolizaDatos>(
      `${this.baseUrl}/${environment.API.SINIESTRO.OBTENER_DATOS_TITULAR}`,
      { headers, params }
    );
  }

  obtenerTiposSiniestro(): Observable<ITipoExpediente[]> {
    const headers = new HttpHeaders();
    return this.http.get<ITipoExpediente[]>(
      `${this.baseUrl}/${environment.API.SINIESTRO.OBTENER_TIPOS_SINIESTROS}`,
      { headers }
    );
  }

  obtenerCoberturas(idTipoExpediente: number): Observable<ICobertura[]> {
    const headers = new HttpHeaders();
    const params = new HttpParams().set(
      'IdTipoExpediente',
      idTipoExpediente.toString()
    );
    return this.http.get<ICobertura[]>(
      `${this.baseUrl}/${environment.API.SINIESTRO.OBTENER_COBERTURAS}`,
      { headers, params }
    );
  }

  aperturaSiniestro(siniestro: ISiniestroRequest): Observable<any> {
    const headers = new HttpHeaders();

    const formData = new FormData();

    // chequear campos requeridos (no se añade un field undefined)
    formData.append('IdPoliza', siniestro.idPoliza.toString());
    if (siniestro.idSiniestroOrigen)
      formData.append('IdSiniestroOrigen', siniestro.idSiniestroOrigen);
    formData.append(
      'PuedeAbrirSiniestro',
      String(siniestro.puedeAbrirSiniestro)
    );
    if (
      siniestro.siniestroCurso !== undefined &&
      siniestro.siniestroCurso !== null
    )
      formData.append('SiniestroCurso', String(siniestro.siniestroCurso));
    formData.append('EstadoPoliza', siniestro.estadoPoliza.toString());
    if (
      siniestro.tipoPersonaApertura !== undefined &&
      siniestro.tipoPersonaApertura !== null
    )
      formData.append(
        'TipoPersonaApertura',
        siniestro.tipoPersonaApertura.toString()
      );
    if (siniestro.nombreCliente)
      formData.append('NombreCliente', siniestro.nombreCliente);

    if (siniestro.numeroDocumento)
      formData.append('NumeroDocumento', siniestro.numeroDocumento);
    if (siniestro.telefonoContacto1)
      formData.append('TelefonoContacto1', siniestro.telefonoContacto1);
    if (siniestro.telefonoContacto2)
      formData.append('TelefonoContacto2', siniestro.telefonoContacto2);
    if (siniestro.email) formData.append('Email', siniestro.email);
    if (siniestro.direccionEntrega)
      formData.append('DireccionEntrega', siniestro.direccionEntrega);
    if (siniestro.codigoPostal)
      formData.append('CodigoPostal', siniestro.codigoPostal);
    if (siniestro.poblacion) formData.append('Poblacion', siniestro.poblacion);
    if (siniestro.provincia) formData.append('Provincia', siniestro.provincia);

    if (
      siniestro.tipoSiniestro !== undefined &&
      siniestro.tipoSiniestro !== null
    )
      formData.append('TipoSiniestro', siniestro.tipoSiniestro.toString());
    if (siniestro.coberturas) {
      siniestro.coberturas.forEach((c) => {
        formData.append('Coberturas', c.toString());
      });
    }
    if (siniestro.comoSeProdujoSiniestro)
      formData.append(
        'ComoSeProdujoSiniestro',
        siniestro.comoSeProdujoSiniestro
      );
    if (siniestro.lugarSuceso)
      formData.append('LugarSuceso', siniestro.lugarSuceso);
    if (siniestro.fechaSuceso)
      formData.append(
        'FechaSuceso',
        siniestro.fechaSuceso instanceof Date
          ? siniestro.fechaSuceso.toISOString()
          : siniestro.fechaSuceso
      );
    if (siniestro.ciudadSuceso)
      formData.append('CiudadSuceso', siniestro.ciudadSuceso);
    if (siniestro.paisSuceso)
      formData.append('PaisSuceso', siniestro.paisSuceso);
    if (siniestro.tipoDispositivo)
      formData.append('TipoDispositivo', siniestro.tipoDispositivo);
    if (siniestro.marca) formData.append('Marca', siniestro.marca);
    if (siniestro.modelo) formData.append('Modelo', siniestro.modelo);
    if (siniestro.numSerie) formData.append('NumSerie', siniestro.numSerie);

    // Serializa auxiliares si es necesario (puedes hacer JSON.stringify)
    if (siniestro.auxiliares && siniestro.auxiliares.length > 0) {
      // En .NET podrías esperar auxiliares como valores tipo array, o un solo string con JSON serializado
      formData.append('Auxiliares', JSON.stringify(siniestro.auxiliares));
    }

    // Agregar los archivos al FormData
    if (siniestro.documentos) {
      siniestro.documentos.forEach((doc, index) => {
        formData.append('Documentos', doc, doc.name);
      });
    }

    return this.http.post<any>(
      `${this.baseUrl}/${environment.API.SINIESTRO.CREAR_SINIESTRO}`,
      formData,
      { headers }
    );
  }

  obtenerDocumentosSiniestro(idSiniestro: number) {
    const headers = new HttpHeaders();
    const params = new HttpParams().set('IdSiniestro', idSiniestro);

    return this.http.get(
      `${this.baseUrl}/${environment.API.SINIESTRO.OBTENER_DOCUMENTOS_SINIESTRO}`,
      { headers, params }
    );
  }

  adjuntarDocumentacion(idSiniestro: number, archivos: File[]) {
    const headers = new HttpHeaders();
    const formData = new FormData();

    formData.append('IdSiniestro', idSiniestro.toString());

    archivos.forEach((archivo) => {
      formData.append('Documentos', archivo);
    });

    return this.http.post(
      `${this.baseUrl}/${environment.API.SINIESTRO.ADJUNTAR_DOCUMENTOS_SINIESTRO}`,
      formData,
      { headers }
    );
  }


}
